import type Konva from "konva";
import { useCallback, useEffect, useRef, type PropsWithChildren } from "react";
import { Stage, type StageProps } from "react-konva";
import { useRefinePanAndZoom } from "../hooks/useRefinePanAndZoom";

export type CanvasProps = {
  dimensions: {
    width: number;
    height: number;
  };
  setDimensions: (dimensions: { width: number; height: number }) => void;
  zoomCenter?: { x: number; y: number };
  contentBounds?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  minZoom?: number;
  maxZoom?: number;
  disablePanning?: boolean;
};

export function ResponsiveStage({
  dimensions,
  setDimensions,
  zoomCenter,
  contentBounds,
  minZoom = 1,
  maxZoom = 10,
  disablePanning = false,
  children,
  ...props
}: PropsWithChildren<CanvasProps & StageProps>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<Konva.Stage>(null);

  const {
    cursor,
    handleMouseMove,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleMouseLeave,
    handleWheel,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd
  } = useRefinePanAndZoom({
    dimensions,
    zoomCenter,
    stageRef,
    contentBounds,
    interactionMode: disablePanning ? "cursor" : "pan",
    isSpaceHeld: false,
    minZoom,
    maxZoom
  });

  // Function to handle resize
  const updateSize = useCallback(() => {
    if (!containerRef.current) return;

    // Get container dimensions
    const containerWidth = containerRef.current.offsetWidth;
    const containerHeight = containerRef.current.offsetHeight;

    // Update state with container dimensions directly
    setDimensions({
      width: containerWidth,
      height: containerHeight
    });
  }, [setDimensions]);

  // Update on mount and observe container size changes
  useEffect(() => {
    updateSize();

    if (!containerRef.current) return undefined;

    const resizeObserver = new ResizeObserver(() => {
      window.requestAnimationFrame(() => updateSize());
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [updateSize]);

  return (
    <div ref={containerRef} className="relative flex size-full">
      <Stage
        ref={stageRef}
        width={Math.max(dimensions.width, 1)}
        height={Math.max(dimensions.height, 1)}
        className="absolute size-full"
        style={{ cursor }}
        onMouseDown={handleDragStart}
        onMouseMove={e => {
          handleMouseMove(e);
          handleDragMove(e);
        }}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleMouseLeave}
        onWheel={handleWheel}
        onTouchStart={handleTouchStart}
        onTouchMove={e => {
          handleTouchMove(e, containerRef);
        }}
        onTouchEnd={handleTouchEnd}
        {...props}
      >
        {children}
      </Stage>
    </div>
  );
}
